import type { HostedReportSummary } from '../types/hostedReport';
import { formatMb } from './hostedReport';

// Tools → HTML Hosting — the words the bundle UI uses, as pure functions so each
// sentence is asserted without rendering (this repo sits close to its jsdom
// timeout cliff, and the wording is the part worth pinning anyway).

export type BundlePhaseView =
  | { phase: 'uploading'; sentBytes: number; totalBytes: number }
  | { phase: 'unpacking'; written: number; total: number }
  | { phase: 'needs_entry'; candidates: string[]; reportId: string };

/** One line of progress text for the create dialog. */
export function bundleProgressLabel(p: BundlePhaseView | null): string | null {
  if (!p) return null;
  if (p.phase === 'uploading') {
    const pct = p.totalBytes > 0 ? Math.min(100, Math.floor((p.sentBytes / p.totalBytes) * 100)) : 0;
    return `Uploading… ${pct}% (${formatMb(p.sentBytes)} of ${formatMb(p.totalBytes)})`;
  }
  if (p.phase === 'unpacking') {
    return p.total > 0 ? `Unpacking… ${p.written} of ${p.total} files` : 'Unpacking…';
  }
  return 'Choose which page to open first.';
}

/** 0-100 for a progress bar, or null when there is nothing to show. */
export function bundleProgressPercent(p: BundlePhaseView | null): number | null {
  if (!p) return null;
  if (p.phase === 'uploading') return p.totalBytes > 0 ? Math.min(100, (p.sentBytes / p.totalBytes) * 100) : 0;
  if (p.phase === 'unpacking') return p.total > 0 ? Math.min(100, (p.written / p.total) * 100) : null;
  return null;
}

/** "12 files · 48 MB" — the storage a bundle report actually holds. */
export function bundleSizeLine(r: Pick<HostedReportSummary, 'kind' | 'bundle' | 'sizeBytes'>): string | null {
  if (r.kind !== 'bundle') return null;
  if (!r.bundle) return `Bundle · ${formatMb(r.sizeBytes)} zip`;
  return `Bundle · ${r.bundle.fileCount} file${r.bundle.fileCount === 1 ? '' : 's'} · ${formatMb(r.bundle.totalBytes)}`;
}

export type BundleStatusBadge = { label: string; help: string } | null;

/** A bundle that is not ready cannot be opened by anyone — say so in the list. */
export function bundleStatusBadge(r: Pick<HostedReportSummary, 'kind' | 'bundleStatus'>): BundleStatusBadge {
  if (r.kind !== 'bundle' || r.bundleStatus === 'ready' || r.bundleStatus === null) return null;
  if (r.bundleStatus === 'extracting') {
    return { label: 'Unpacking', help: 'The zip is being unpacked. The link works once it is ready.' };
  }
  return {
    label: 'Upload incomplete',
    help: 'The zip never finished uploading, so the link does not work. Delete this and upload again.',
  };
}

/** Why a bundle cannot be downloaded or have its file replaced (not built for bundles). */
export const BUNDLE_NO_DOWNLOAD_HELP =
  'Bundles cannot be downloaded as one file. Keep the original folder, or upload a new bundle to replace this one.';
